import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Link } from 'react-router-dom';

// --- Helper Functions & Components ---

// Fix for default Leaflet icon path issue with bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const formatPrice = (price) => {
  const num = parseInt(price, 10);
  if (isNaN(num)) return 'N/A';
  return `UGX ${num.toLocaleString()}`;
};

// Component to automatically adjust map bounds to fit all markers
const RecenterAutomatically = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);
  return null;
};

// The popup that appears at the bottom of the map when a marker is selected
const PropertyPopupCard = ({ property, onSelect }) => (
  <div className="absolute bottom-4 left-4 right-4 bg-card border border-border rounded-lg shadow-lg p-4 z-[1000] animate-in slide-in-from-bottom-5 duration-300">
    <div className="flex items-start space-x-3">
      <img
        src={property?.title_image}
        alt={property?.title}
        className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
        onError={(e) => { e.target.src = '/assets/images/no_image.png'; }}
      />
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-foreground truncate">
          <Link to={`/property/${property.slug_id}`} className="hover:underline">
            {property?.title}
          </Link>
        </h3>
        <p className="text-sm text-muted-foreground truncate">{property?.address}</p>
        <div className="flex items-center justify-between mt-2">
          <span className="text-lg font-semibold text-primary">
            {formatPrice(property?.price)}
          </span>
        </div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onSelect(null)}
        className="flex-shrink-0"
      >
        <Icon name="X" size={16} />
      </Button>
    </div>
  </div>
);

// --- Main MapView Component ---

const MapView = ({ properties, onPropertySelect, selectedProperty }) => {
  // Filter out properties that don't have valid coordinates
  const validProperties = useMemo(() => 
    properties?.filter(p => p.latitude && p.longitude) || [], 
    [properties]
  );

  // Calculate the geographical bounds of the valid properties
  const bounds = useMemo(() => {
    const b = L.latLngBounds();
    validProperties.forEach(p => {
      b.extend([parseFloat(p.latitude), parseFloat(p.longitude)]);
    });
    return b;
  }, [validProperties]);

  // Default center if no properties are available
  const defaultCenter = [0.3476, 32.5825]; // Kampala, Uganda

  return (
    <div className="relative w-full h-full bg-muted rounded-lg overflow-hidden">
      <MapContainer
        center={defaultCenter}
        zoom={13}
        scrollWheelZoom={true}
        className="w-full h-full z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {validProperties.map((property) => (
          <Marker
            key={property.id}
            position={[parseFloat(property.latitude), parseFloat(property.longitude)]}
            eventHandlers={{
              click: () => {
                onPropertySelect(property);
              },
            }}
          >
            <Popup>
              <div className="w-48">
                <img src={property.title_image} alt={property.title} className="w-full h-24 object-cover rounded-md mb-2" />
                <h4 className="font-bold text-sm truncate">{property.title}</h4>
                <p className="text-xs text-gray-600">{property.address}</p>
                <p className="font-semibold text-primary mt-1">{formatPrice(property.price)}</p>
              </div>
            </Popup>
          </Marker>
        ))}

        {bounds.isValid() && <RecenterAutomatically bounds={bounds} />}
      </MapContainer>

      {/* Selected Property Popup Card */}
      {selectedProperty && (
        <PropertyPopupCard property={selectedProperty} onSelect={onPropertySelect} />
      )}

      {/* No Properties Message */}
      {validProperties.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10 pointer-events-none">
          <div className="text-center p-4">
            <Icon name="MapOff" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Properties Found on Map</h3>
            <p className="text-muted-foreground">Try adjusting your search filters or check if properties have location data.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapView;